import { Box, Flex, Text } from '@chakra-ui/react';
import { ArrowDownBold, ArrowRightBold, Badge, NavSection } from '@aslanpay/portal-common-lib';
import { ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import BrandLogo from 'app/components/Branding/BrandLogo';
import PoweredByAslan from 'app/components/Branding/PoweredByAslan';

import { useCompanyBranding, useSelectedColours } from 'app/contexts/Branding';

import SidebarShell from '../SidebarShell';

const MUTED = 'brand.navMuted';
const ROW_HEIGHT = '38px';

interface AdminSidebarProps {
  sections: NavSection[];
  activeId: string;
  companyName: string;
  navbarContent?: ReactNode;
}

function findGroupIdForItem(sections: NavSection[], activeId: string): string | undefined {
  return sections.find((section) => section.items.some((item) => item.id === activeId))?.id;
}

function NavRow({
  item,
  isActive,
  onNavigate,
}: {
  item: NavSection['items'][number];
  isActive: boolean;
  onNavigate: () => void;
}) {
  const selected = useSelectedColours();

  return (
    <Flex
      as={RouterLink}
      to={item.path}
      onClick={onNavigate}
      aria-current={isActive ? 'page' : undefined}
      align="center"
      gap="8px"
      w="full"
      minH={ROW_HEIGHT}
      px="10px"
      py="4px"
      borderRadius="6px"
      position="relative"
      overflow="hidden"
      textStyle="smallMedium"
      bg={isActive ? selected.fill : 'transparent'}
      color={isActive ? selected.text : MUTED}
      transition="background 0.15s ease, color 0.15s ease"
      _hover={{
        bg: isActive ? selected.fill : 'brand.whiteAlpha10',
        color: isActive ? selected.text : 'brand.white',
        textDecoration: 'none',
      }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'brand.white', outlineOffset: '-2px' }}
      data-testid={`admin-nav-${item.id}`}
    >
      {/* The active purple only reaches ~1.6:1 against the dark rail, so a gold edge
          carries the active state rather than colour alone. */}
      {isActive && (
        <Box
          position="absolute"
          left={0}
          top={0}
          bottom={0}
          w="4px"
          bg="system.warning"
          aria-hidden="true"
          data-testid={`admin-nav-accent-${item.id}`}
        />
      )}
      <Flex flexShrink={0} align="center" sx={{ svg: { boxSize: '18px' } }} aria-hidden="true">
        {isActive && item.iconActive ? item.iconActive : item.icon}
      </Flex>
      <Text isTruncated>{item.label}</Text>
    </Flex>
  );
}

function SidebarBody({
  sections,
  activeId,
  companyName,
  onNavigate,
}: Omit<AdminSidebarProps, 'navbarContent'> & { onNavigate: () => void }) {
  const activeGroupId = findGroupIdForItem(sections, activeId);
  const [openGroupId, setOpenGroupId] = useState<string | undefined>(activeGroupId);

  // Follow the page: whichever group owns the current item is the one left open, so the
  // active row is never hidden behind a collapsed header.
  useEffect(() => {
    if (activeGroupId) {
      setOpenGroupId(activeGroupId);
    }
  }, [activeGroupId]);

  const visibleSections = sections.filter((section) => !section.hidden);

  return (
    <Flex direction="column" h="full" minH={0}>
      {/* Pinned: the header never scrolls away. */}
      <Flex direction="column" align="center" gap="6px" p="20px 16px 12px" flexShrink={0}>
        <Box data-testid="admin-context-label">
          <Badge status="admin" value="ADMIN" size="sm" />
        </Box>
        <Text textStyle="extraSmallRegular" color="brand.white" mt="6px">
          Company
        </Text>
        <Text textStyle="h6SemiBold" color="brand.white" textAlign="center" noOfLines={2}>
          {companyName}
        </Text>
      </Flex>

      {/* The only scrolling region — and with one group open it rarely needs to. */}
      <Flex
        as="nav"
        aria-label="Main navigation"
        direction="column"
        gap="2px"
        flex="1"
        minH={0}
        overflowY="auto"
        px="12px"
        pb="12px"
        css={{
          '&::-webkit-scrollbar': { width: '3px' },
          '&::-webkit-scrollbar-thumb': {
            background: 'var(--chakra-colors-brand-mediumPurple)',
            borderRadius: '5px',
          },
        }}
      >
        {visibleSections.map((section) => {
          const isOpen = openGroupId === section.id;

          return (
            <Box key={section.id} data-testid={`admin-section-${section.id}`}>
              <Flex
                as="button"
                type="button"
                align="center"
                gap="6px"
                w="full"
                minH={ROW_HEIGHT}
                px="10px"
                borderRadius="6px"
                color="brand.white"
                textStyle="smallMedium"
                aria-expanded={isOpen}
                // One group at a time: opening this one closes whichever was open.
                onClick={() => setOpenGroupId(isOpen ? undefined : section.id)}
                _hover={{ bg: 'brand.whiteAlpha10' }}
                _focusVisible={{
                  outline: '2px solid',
                  outlineColor: 'brand.white',
                  outlineOffset: '-2px',
                }}
                data-testid={`admin-section-header-${section.id}`}
              >
                <Box as={isOpen ? ArrowDownBold : ArrowRightBold} boxSize="16px" aria-hidden />
                <Text isTruncated>{section.label}</Text>
              </Flex>

              {isOpen && (
                <Flex direction="column" gap="2px" pl="8px" pb="4px">
                  {section.items.map((item) => (
                    <NavRow
                      key={item.id}
                      item={item}
                      isActive={activeId === item.id}
                      onNavigate={onNavigate}
                    />
                  ))}
                </Flex>
              )}
            </Box>
          );
        })}
      </Flex>

      {/* Pinned: the logo stays on the floor of the sidebar. */}
      <Flex
        align="center"
        justify="center"
        h="56px"
        flexShrink={0}
        borderTop="1px solid"
        borderColor="brand.purple2"
      >
        <BrandLogo variant="onDark" w="90px" maxH="36px" />
      </Flex>
    </Flex>
  );
}

/** The admin portal's nav: three collapsible groups, only one open at a time. */
function AdminSidebar({
  sections,
  activeId,
  companyName,
  navbarContent = undefined,
}: AdminSidebarProps) {
  const { isBranded } = useCompanyBranding();

  return (
    <SidebarShell
      data-testid="admin-sidebar"
      bg="brand.darkPurple"
      controlColor="brand.white"
      mobileHeader={
        <>
          {/* A branded company leads with its logo; unbranded, the header is unchanged. On a
              narrow phone the logo is what gives way, so the badge never reaches the menu button. */}
          {isBranded && (
            <BrandLogo variant="onDark" maxW="96px" maxH="32px" minW={0} flexShrink={1} />
          )}
          {navbarContent}
          <Box data-testid="admin-context-label-mobile" flexShrink={0}>
            <Badge status="admin" value="ADMIN" size="sm" />
          </Box>
        </>
      }
      footer={<PoweredByAslan variant="onDark" justify="center" pb="16px" />}
    >
      {({ onNavigate }) => (
        <SidebarBody
          sections={sections}
          activeId={activeId}
          companyName={companyName}
          onNavigate={onNavigate}
        />
      )}
    </SidebarShell>
  );
}

export default AdminSidebar;
